/**
 * Polish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Polish at a mathematician's register. **Warrant → «Uzasadnienie»**
 * (justification — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / few / many / other, with «ograniczenie» declined (1 ograniczenie ·
 * 2–4 ograniczenia · 5+ ograniczeń), so `frameActive` declares exactly those (plus an explicit
 * `=0`). Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay
 * fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const PL_CATALOG = {
    locale: 'pl',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Badanie',
        [P2.sectionClaims]: 'Twierdzenia',
        [P2.sectionIntegrity]: 'Integralność',
        [P2.axisWarrantLabel]: 'Uzasadnienie',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empiryczne',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definicja} interp {Interpretacja} testable {Testowalne} tested {Przetestowane} refuted {Obalone} program {Program badawczy} philos {Filozoficzne} other {Nieznane}}',
        [P2.axisModalLabel]: 'Modalne',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Poziom siły {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Eksplorowalne',
        [P2.stateRefuted]: 'Obalone',
        [P2.stateOutside]: 'Poza zakresem',
        [P2.verdictConflict]: 'W konflikcie z {constraints}',
        [P2.verdictOutside]: 'Wymaga {missing}',
        [P2.lossRegisterPlain]: 'Uproszczone do języka potocznego — część precyzji jest pominięta.',
        [P2.lossRegisterKid]: 'Uproszczone dla młodszych czytelników — wiele szczegółów pominięto.',
        [P2.lossProfileDetail]: 'Pełny profil Uzasadnienie / Empiryczne / Modalne jest tu podsumowany.',
        [P2.lossConflictDetail]: 'Szczegóły konfliktu są skrócone w tym widoku.',
        [P2.laneVerified]: 'Zweryfikowane',
        [P2.laneUnverified]: 'Niezweryfikowane',
        [P2.laneConflict]: 'Konflikt udowodniony',
        [P2.constraintLogic]: 'Logika',
        [P2.constraintLightBends]: 'Światło ugina się w polu grawitacyjnym',
        [P2.constraintParallelPostulate]: 'Postulat równoległości',
        [P2.claimScalarGravity]: 'Skalarna teoria grawitacji',
        [P2.claimNonEuclidean]: 'Geometria nieeuklidesowa',
        [P2.claimSubstrateSr]: 'Substratowa interpretacja szczególnej teorii względności',
        [P2.edgeExtObservation]: 'Obserwacja zewnętrzna',
        [P2.edgeResourceSample]: 'Współdzielony zasób obliczeniowy',
        [P2.edgeHeuristicSample]: 'Wskazówka heurystyczna',
        [P2.edgeSharedSample]: 'Wspólny fundament',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ograniczenia',
        [P2.sectionEdges]: 'Krawędzie',
        [P2.sectionFrame]: 'Rama',
        [P2.sectionExposure]: 'Ekspozycja',
        [P2.statusAdoptedFormal]: 'Przyjęte (formalne)',
        [P2.statusAdoptedInterpretive]: 'Przyjęte (interpretacyjne)',
        [P2.statusSuspended]: 'Zawieszone',
        [P2.edgeEffectConstraint]: 'Tworzy ograniczenie (może obalić)',
        [P2.edgeEffectResource]: 'Zasób — bez wpływu na werdykty',
        [P2.edgeEffectHeuristic]: 'Heurystyka — bez wpływu na werdykty',
        [P2.edgeEffectShared]: 'Wspólny fundament — bez wpływu na werdykty',
        [P2.frameActive]: '{count, plural, =0 {Brak aktywnych ograniczeń} one {# aktywne ograniczenie} few {# aktywne ograniczenia} many {# aktywnych ograniczeń} other {# aktywnego ograniczenia}}',
        [P2.exposureNone]: 'Brak zależności interpretacyjnych',
        [P2.exposureDep]: 'Zależy od {id}',
        [P2.laneTrackMachine]: 'Maszyna',
        [P2.laneTrackExpert]: 'Ekspert',
        [P2.laneTrackCommunity]: 'Społeczność',
        [P2.expertAudited]: 'Poddane audytowi',
        [P2.expertInProgress]: 'W toku',
        [P2.expertNotReviewed]: 'Nieprzejrzane',
        [P2.communityFlags]: 'Istnieją oznaczenia',
        [P2.communityNone]: 'Brak oznaczeń',
        [P2.controlChallenge]: 'Zakwestionuj',
        [P2.controlActivate]: 'Aktywuj',
        [P2.controlMakeInterpretive]: 'Uczyń interpretacyjnym',
        [P2.controlImportEdge]: 'Importuj krawędź',
        [P2.controlPromote]: 'Awansuj',
        [P2.controlUndo]: 'Cofnij',
        [P2.controlRedo]: 'Ponów',
        [P2.controlRegister]: 'Zarejestruj',
        [P2.promoteRejected]: 'Awans odrzucony',
        [P2.promoteAccepted]: 'Awans przyjęty',
        [P2.promoteReasonRefutationTerminal]: 'Dowód nie może uleczyć obalenia.',
        [P2.promoteReasonKeystone]: 'Awans kluczowy wymaga wsparcia empirycznego.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Typ empiryczny nie jest ustalony.',
        [P2.promoteReasonModalNotFixed]: 'Stopień modalny nie jest ustalony.',
        [P2.promoteReasonWarrantNotImproved]: 'Stopień uzasadnienia nie uległ poprawie.',
        [P2.evidenceComputation]: "{engine} obliczył {op} = {result} przy {config}",
        [P2.lossNumericRounding]: "Wynik zaokrąglony do {dp} miejsc dziesiętnych",
    },
};
//# sourceMappingURL=pl.js.map